"use client";

import { Button } from "@bandwith/ui";
import { ChevronRight, Play, RotateCcw, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { playChordFromDefinition, playStringNote } from "../chordAudio";
import {
  type ChordIdentificationResult,
  createInitialState,
  type FretboardState,
  generateDefineDirective,
  generateInlineChord,
  identifyChord,
} from "./ChordIdentifier";
import { InteractiveFretboard } from "./InteractiveFretboard";

/** Convert relative frets (from ChordIdentificationResult) to absolute fret positions */
function toAbsoluteFrets(result: ChordIdentificationResult): (number | "x")[] {
  return result.frets.map((f) => {
    if (f === "x" || f === 0) return f;
    return f + result.baseFret - 1;
  });
}

interface ChordFinderProps {
  isOpen: boolean;
  onClose: () => void;
  onInsertChord: (text: string) => void;
  onInsertDefinition: (text: string) => void;
}

/**
 * Format chord name for display: GM → G for major chords.
 * Tonal.js uses uppercase "M" for major (GM, AM) and lowercase "m" for minor (Gm, Am),
 * so we can safely strip trailing uppercase "M" without affecting minor chords.
 */
export function formatChordName(name: string): string {
  if (name.endsWith("M")) {
    return name.slice(0, -1);
  }
  return name;
}

export function ChordFinder({
  isOpen,
  onClose,
  onInsertChord,
  onInsertDefinition,
}: ChordFinderProps) {
  const [fretboardState, setFretboardState] =
    useState<FretboardState>(createInitialState);
  const [result, setResult] = useState<ChordIdentificationResult | null>(null);
  // Custom name: only set by user typing, never auto-filled
  const [customName, setCustomName] = useState<string>("");
  // Selected from detected chords dropdown, auto-selects first match
  const [selectedDetected, setSelectedDetected] = useState<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Final chord name: custom overrides detected
  const finalChordName = customName || selectedDetected;

  // Identify chord whenever fretboard state changes
  useEffect(() => {
    const newResult = identifyChord(fretboardState);
    setResult(newResult);

    // Auto-select first detected chord, or clear if none available
    const firstChord = newResult.chordNames[0];
    setSelectedDetected(firstChord ?? "");
  }, [fretboardState]);

  // Play chord with debouncing when fretboard changes
  const handleFretboardChange = useCallback((newState: FretboardState) => {
    setFretboardState(newState);

    // Debounce audio playback
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const tempResult = identifyChord(newState);
      if (tempResult.notes.length > 0) {
        playChordFromDefinition({
          name: "preview",
          baseFret: 1,
          frets: toAbsoluteFrets(tempResult),
        });
      }
    }, 100);
  }, []);

  const handleClear = useCallback(() => {
    setFretboardState(createInitialState());
    setCustomName("");
    setSelectedDetected("");
  }, []);

  const handlePlay = useCallback(() => {
    if (!result || result.notes.length === 0) return;

    playChordFromDefinition({
      name: finalChordName || "custom",
      baseFret: 1,
      frets: toAbsoluteFrets(result),
    });
  }, [result, finalChordName]);

  // Handle strumming - play individual strings as they're crossed
  const handleStrum = useCallback(
    (stringIndices: number[]) => {
      if (!result) return;
      for (const stringIndex of stringIndices) {
        const fret = result.frets[stringIndex];
        if (fret === undefined) continue;
        // Convert relative fret to absolute
        const absoluteFret =
          fret === "x" || fret === 0 ? fret : fret + result.baseFret - 1;
        playStringNote(stringIndex, absoluteFret, {
          duration: 1.5,
          volume: 0.25,
        });
      }
    },
    [result],
  );

  const handleInsertChord = useCallback(() => {
    if (!finalChordName) return;
    onInsertChord(generateInlineChord(formatChordName(finalChordName)));
    onClose();
  }, [finalChordName, onInsertChord, onClose]);

  const handleInsertDefinition = useCallback(() => {
    if (!result) return;
    const name = finalChordName ? formatChordName(finalChordName) : "Custom";
    onInsertDefinition(generateDefineDirective(result, name));
    onClose();
  }, [result, finalChordName, onInsertDefinition, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFretboardState(createInitialState());
      setCustomName("");
      setSelectedDetected("");
      setResult(null);
    }
  }, [isOpen]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const hasNotes = result && result.notes.length > 0;
  const hasChordName = finalChordName.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative bg-bg-secondary rounded-lg shadow-xl w-full max-w-sm mx-4 max-h-[90vh] flex flex-col border border-border-subtle">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-subtle">
          <h2 className="text-lg font-semibold text-text-primary">
            Find Chord
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClear}
              className="p-1.5 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Clear"
              title="Clear"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handlePlay}
              disabled={!hasNotes}
              className="p-1.5 text-text-muted hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Play"
              title="Play"
            >
              <Play className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Fretboard */}
          <div className="border-b border-border-subtle">
            <InteractiveFretboard
              state={fretboardState}
              onChange={handleFretboardChange}
              fingering={result?.fingering}
              onStrum={handleStrum}
            />
          </div>

          {/* Results */}
          <div className="p-4 space-y-3">
            {/* Notes per string aligned horizontally */}
            {result && (
              <div className="flex justify-center gap-1 font-mono text-xs">
                {result.notesPerString.map((note, i) => (
                  // String position is stable (0-5 for each guitar string, never reordered)
                  // biome-ignore lint/suspicious/noArrayIndexKey: Fixed array of 6 guitar strings
                  <div key={i} className="w-7 text-center text-text-muted">
                    {note ?? "×"}
                  </div>
                ))}
              </div>
            )}

            {/* Chord name selection */}
            <div className="flex items-center gap-2">
              <select
                value={selectedDetected}
                onChange={(e) => setSelectedDetected(e.target.value)}
                disabled={!result || result.chordNames.length === 0}
                className="flex-1 bg-bg-tertiary text-text-secondary font-mono rounded px-2 py-1.5 text-sm
                           border border-border-subtle focus:border-amber focus:outline-none cursor-pointer
                           disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Detected chord names"
              >
                {result && result.chordNames.length > 0 ? (
                  result.chordNames.map((name) => (
                    <option key={name} value={name}>
                      {formatChordName(name)}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No matches
                  </option>
                )}
              </select>
              <span className="text-text-muted text-sm">or</span>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Custom"
                disabled={!hasNotes}
                className="w-24 bg-bg-tertiary text-amber font-mono rounded px-2 py-1.5 text-sm
                           border border-border-subtle focus:border-amber focus:outline-none
                           placeholder:text-text-muted disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Custom chord name"
              />
              <ChevronRight className="w-4 h-4 text-text-muted shrink-0" />
              <button
                type="button"
                onClick={handlePlay}
                disabled={!hasNotes}
                className="font-mono text-amber min-w-[3ch] cursor-pointer hover:underline disabled:no-underline disabled:cursor-default"
              >
                {hasChordName ? formatChordName(finalChordName) : "—"}
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-border-subtle space-y-2">
          <Button
            onClick={handleInsertChord}
            disabled={!hasChordName}
            className="w-full"
          >
            Insert Chord Reference [
            {hasChordName ? formatChordName(finalChordName) : "..."}]
          </Button>
          <Button
            variant="secondary"
            onClick={handleInsertDefinition}
            disabled={!hasNotes}
            className="w-full"
          >
            Insert Chord Diagram
          </Button>
        </div>
      </div>
    </div>
  );
}
